/**
 * Portal v2 failures (`/portal/failures`).
 *
 * Answers "why is mail failing", which is a different question from "how much
 * is failing" — the reports pages already cover volume. The split between
 * SPF-only, DKIM-only and both matters because each leads somewhere different:
 * a missing SPF include, a signing/selector problem, or an unauthorised sender.
 *
 * Picking a reason filters the source list, so a customer can go straight from
 * "DKIM is the problem" to "and these are the senders responsible".
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { Skeleton } from "@ddmarc/ui";
import { portalApi, type FailureReason } from "@/lib/portal-api";
import { usePortalScope } from "@/lib/portal-scope-context";
import { CardV2, KpiTile, SectionHeader, StatPill } from "@/components/primitives";
import { cn } from "@/lib/utils";

const failureRateTone = (pct: number): "good" | "warn" | "danger" =>
  pct <= 5 ? "good" : pct <= 20 ? "warn" : "danger";

export function PortalFailuresV2Page() {
  const { selectedDomainId } = usePortalScope();
  const [reason, setReason] = useState<FailureReason | null>(null);

  const { data: summary, isLoading } = useQuery({
    queryKey: ["portal", "failures", "summary", selectedDomainId],
    queryFn: () => portalApi.getFailureSummary({ domain_id: selectedDomainId }),
  });

  const { data: sources, isLoading: sourcesLoading } = useQuery({
    queryKey: ["portal", "failures", "sources", selectedDomainId, reason],
    queryFn: () =>
      portalApi.getFailureSources({ domain_id: selectedDomainId, reason, size: 25 }),
  });

  return (
    <div className="space-y-3.5">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            Failures
          </span>
        }
        subtitle="Why messages are failing authentication, and which sources are responsible."
      />

      <div className="grid gap-3 md:grid-cols-3">
        <KpiTile
          label="Messages"
          value={isLoading ? <Skeleton className="h-7 w-16" /> : (summary?.total_emails ?? 0).toLocaleString()}
          hint="Last 30 days"
        />
        <KpiTile
          label="Failing"
          value={
            isLoading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              (summary?.failed_emails ?? 0).toLocaleString()
            )
          }
          hint="Did not pass DMARC"
        />
        <KpiTile
          label="Failure rate"
          value={
            isLoading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              <StatPill tone={failureRateTone(summary?.failure_rate ?? 0)}>
                {(summary?.failure_rate ?? 0).toFixed(1)}%
              </StatPill>
            )
          }
          hint="Of all reported mail"
        />
      </div>

      <CardV2
        title="What is failing"
        subtitle="Each cause points at a different fix. Select one to filter the sources below."
      >
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : !summary?.by_reason.length ? (
          <p className="py-6 text-center text-[12.5px] text-muted-foreground">
            No authentication failures in this period.
          </p>
        ) : (
          <div
            role="radiogroup"
            aria-label="Filter sources by failure cause"
            className="grid gap-2 md:grid-cols-3"
          >
            {summary.by_reason.map((row) => {
              const active = reason === row.reason;
              return (
                <button
                  key={row.reason}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setReason(active ? null : row.reason)}
                  className={cn(
                    "rounded-lg border p-3 text-left transition-colors",
                    active
                      ? "border-[var(--color-primary)] bg-secondary"
                      : "border-border hover:bg-secondary/60"
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[12.5px] font-medium text-foreground">
                      {row.display_name}
                    </span>
                    <span className="tnum text-[13px] font-semibold text-foreground">
                      {row.percentage.toFixed(0)}%
                    </span>
                  </div>
                  <p className="mt-1 text-[11.5px] leading-snug text-muted-foreground">
                    {row.description}
                  </p>
                  <p className="mt-1.5 tnum text-[11.5px] text-muted-foreground">
                    {row.count.toLocaleString()} messages
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </CardV2>

      <CardV2
        title={reason ? "Sources with this failure" : "Sources with failures"}
        subtitle={
          reason
            ? "Filtered — select the same cause again to clear."
            : "Ranked by failing volume."
        }
        bodyClassName="p-0"
      >
        {sourcesLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !sources?.items.length ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <AlertTriangle className="h-8 w-8 text-[var(--v2-fg-faint)]" />
            <p className="text-[12.5px] text-muted-foreground">
              No sources match this filter.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            <div className="grid grid-cols-[1.3fr_1.2fr_0.6fr_0.7fr_0.7fr_0.7fr] gap-3 px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
              <span>Source IP</span>
              <span>Organization</span>
              <span>Country</span>
              <span className="text-right">Messages</span>
              <span className="text-right">Failing</span>
              <span className="text-right">Rate</span>
            </div>
            {sources.items.map((item) => (
              <div
                key={item.source_ip}
                className="grid grid-cols-[1.3fr_1.2fr_0.6fr_0.7fr_0.7fr_0.7fr] items-center gap-3 px-4 py-[var(--v2-row-py,0.625rem)] text-[12.5px]"
              >
                <Link
                  to="/portal/senders/$sourceIp"
                  params={{ sourceIp: item.source_ip }}
                  className="truncate font-mono-data text-foreground underline-offset-2 hover:underline"
                >
                  {item.source_ip}
                </Link>
                <span className="truncate text-muted-foreground">{item.asn_org ?? "—"}</span>
                <span className="text-foreground">{item.country ?? "—"}</span>
                <span className="tnum text-right text-foreground">
                  {item.total_emails.toLocaleString()}
                </span>
                <span className="tnum text-right text-[var(--v2-danger)]">
                  {item.failed_emails.toLocaleString()}
                </span>
                <span className="text-right">
                  <StatPill tone={failureRateTone(item.failure_rate)}>
                    {item.failure_rate.toFixed(0)}%
                  </StatPill>
                </span>
              </div>
            ))}
          </div>
        )}
      </CardV2>
    </div>
  );
}
